import * as React from 'react';
import { cn } from '@/lib/utils';
import { CDPageContainer } from '../primitives/CDPageContainer';
import {
  type CalculatingCashToCloseData,
  CDCalculatingCashToClose,
} from '../sections/CDCalculatingCashToClose';
import {
  CDSummariesOfTransactions,
  type SummariesOfTransactionsData,
} from '../sections/CDSummariesOfTransactions';

export interface CDPage3Data {
  calculatingCashToClose: Partial<CalculatingCashToCloseData>;
  summariesOfTransactions: Partial<SummariesOfTransactionsData>;
}

export interface CDPage3Props {
  /** Page 3 data */
  data: Partial<CDPage3Data>;
  /** Total number of pages */
  totalPages?: number;
  /** Form ID for footer */
  formId?: string;
  /** Whether fields are editable */
  editable?: boolean;
  /** onChange handler for data updates */
  onChange?: (data: Partial<CDPage3Data>) => void;
  /** Additional className */
  className?: string;
}

/**
 * CDPage3 - Complete Page 3 of Closing Disclosure
 *
 * Contains:
 * - Calculating Cash to Close (comparison table)
 * - Summaries of Transactions (Borrower & Seller side by side)
 * - Page footer
 */
export const CDPage3 = React.forwardRef<HTMLDivElement, CDPage3Props>(
  ({ data, totalPages = 5, formId, editable = false, onChange, className }, ref) => {
    const handleCashToCloseChange = (
      calculatingCashToClose: Partial<CalculatingCashToCloseData>,
    ) => {
      onChange?.({ ...data, calculatingCashToClose });
    };

    const handleSummariesChange = (
      summariesOfTransactions: Partial<SummariesOfTransactionsData>,
    ) => {
      onChange?.({ ...data, summariesOfTransactions });
    };

    return (
      <CDPageContainer
        ref={ref}
        pageNumber={3}
        totalPages={totalPages}
        formId={formId}
        className={cn(className)}
      >
        {/* Calculating Cash to Close */}
        <CDCalculatingCashToClose
          data={data.calculatingCashToClose || {}}
          editable={editable}
          onChange={handleCashToCloseChange}
        />

        {/* Summaries of Transactions */}
        <div className="mt-4">
          <CDSummariesOfTransactions
            data={data.summariesOfTransactions || {}}
            editable={editable}
            onChange={handleSummariesChange}
          />
        </div>
      </CDPageContainer>
    );
  },
);

CDPage3.displayName = 'CDPage3';
